import { useCallback, useState } from 'react';
import { useAccountKey } from '../context/AccountKeyContext';
import { useSafe } from '../context/SafeContext';

/**
 * Shown when account-key recovery failed on this browser (a stale/corrupt local
 * wrap, typically after a password change) — a persistent condition the unlock
 * path can't self-heal because it only holds the local wrapping key. Without a
 * prompt, the account key is silently absent and the user hits confusing
 * downstream errors ("Finishing account setup — try again in a moment." on
 * create-family, "(unable to decrypt)" category names). A full sign-in re-derives
 * the account key from the server wrap and rewrites the local wrap.
 *
 * Rendered in AppShell's banner stack (authenticated + unlocked only), so no auth
 * check is needed here. Not dismissible: ignoring it leaves family features and
 * shared-item decryption broken until the user signs in again.
 */
export function AccountSetupBanner(): JSX.Element | null {
  const { recoveryFailed } = useAccountKey();
  const { logout } = useSafe();
  const [signingOut, setSigningOut] = useState(false);

  const signInAgain = useCallback(async () => {
    setSigningOut(true);
    try {
      await logout(); // clears the session → the /auth login form
    } catch {
      setSigningOut(false);
    }
  }, [logout]);

  if (!recoveryFailed) return null;

  return (
    <div
      role="alert"
      className="bg-hbs-danger/10 border-b border-hbs-danger/40 px-4 py-2 flex items-center justify-between gap-4"
    >
      <span className="text-sm text-hbs-text">
        Your saved session is out of date, so account setup couldn’t finish (this
        can happen after a password change). Sign in again to restore access to
        Families and shared items.
      </span>
      <button
        id="account-setup-sign-in-again-button"
        type="button"
        onClick={signInAgain}
        disabled={signingOut}
        className="shrink-0 inline-flex items-center justify-center h-8 px-3 rounded-hbs-sm text-[13px] font-bold bg-hbs-primary text-hbs-on-primary hover:bg-hbs-primary-hover disabled:opacity-50"
      >
        {signingOut ? 'Signing out…' : 'Sign in again'}
      </button>
    </div>
  );
}
